import React, { useContext, useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SmallSectionHeader, Title } from "../Typography/Typography";
import { SelectBoxV2 } from "../SelectBoxV2/SelectBoxV2";
import { PrimaryButtonFitContainer } from "../Buttons/Buttons";
import { ProductApplicationSelect } from "../ProductApplicationSelect/ProductApplicationSelect";
import { useStoreState } from "../../util/util";
import { Notifications } from "../Notifications/NotificationsContext";
import { Form, FormGrid2x2 } from "../../layout/FormLayout";
import { TextField } from "../TextFields/TextFields";
import { endpoints } from "../../endpoints";
import {
  packagingTypeOrUnitToOption,
  usePackagingUnits,
} from "../../util/SkuUtils";
import { convertProductSKUToOption } from "../../util/util";
import { SAMPLE_REQUEST_PURPOSES } from "../../pages/public/AddToSampleRequestForm/AddToSampleRequestForm";
import type { PIMProductBase } from "../../types/types.PIM";
import { SearchSelectInfiniteScroll } from "../SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import axios from "axios";
import { strings } from "../../util/strings";
import type { OptionType } from "../../types/types";
import { KeyValueDisplay } from "../KeyValueDisplay";
import { formatTotalQuantityString } from "../quoteCart/BuyerQuoteItemForm";

type AddProductToSampleRequestProps = {
  transactionId: string;
  buyerId?: string;
  transactionType: "sample-requests";
  onComplete: () => void;
};

export const AddProductToSampleRequest: React.FC<AddProductToSampleRequestProps> =
  ({ buyerId, transactionId, onComplete }) => {
    const { t } = useTranslation();
    const {
      tenant_id,
      storefront_id,

      storefront_metadata: { generic_sku_packaging_type_id },
    } = useStoreState();
    const { notifySuccess, notifyError } = useContext(Notifications);
    const [selectedProduct, setSelectedProduct] =
      useState<PIMProductBase | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const { packagingUnits } = usePackagingUnits();
    const methodsOfUseForm = useForm();

    const {
      control,
      handleSubmit,
      setValue,
      formState,
      register,
      setError,
      clearErrors,
      watch,
    } = methodsOfUseForm;
    const { errors } = formState;

    const { no_of_units, packaging } = watch(["no_of_units", "packaging"]);

    useEffect(() => {
      register({
        name: "product_id",
        required: strings(t).thisIsARequiredField,
      });
    }, [register, t]);

    const handleProductChange = (option: OptionType<PIMProductBase>) => {
      setValue("product_id", option.value.id);
      if (errors["product_id"]) {
        clearErrors("product_id");
      }
      if (selectedProduct) {
        setValue("unit_of_measure", { value: undefined, label: undefined });
        setValue("packaging", { value: undefined, label: undefined });
        setValue("no_of_units", null);
      }
      setSelectedProduct(option.value);
    };

    const productSkuOptions = selectedProduct
      ? selectedProduct.product_skus
          .filter(
            (sku) =>
              !!sku.is_sample &&
              sku.packaging_type.id !== generic_sku_packaging_type_id
          )
          .map(convertProductSKUToOption)
      : [];

    const unitOfMeasureOptions = packagingUnits
      ? packagingUnits.map(packagingTypeOrUnitToOption)
      : [];

    const onSubmit = async (data: any) => {
      try {
        setIsSaving(true);

        if (!selectedProduct) {
          setError("product_id", {
            type: "required",
            message: strings(t).thisIsARequiredField,
          });
          setIsSaving(false);
          return;
        }

        const selectedSku = selectedProduct.product_skus.find(
          (sku) => sku.id === data.packaging.value.id
        );

        if (!selectedSku) {
          notifyError(t("Selected SKU not found"));
          setIsSaving(false);
          return;
        }

        const basePayload = {
          product_id: selectedProduct.id,
          sku_id: selectedSku.id,
          no_of_units: data.no_of_units,
          sample_quantity: calculateTotalQuantity(
            selectedSku.package_volume,
            data.no_of_units
          ),
          total_quantity: data.total_annual_quantity,
          total_quantity_packaging_unit_id: data.unit_of_measure.value,
          purpose: data.purpose.value,
        };

        const payload =
          data.application?.value && data.application.value !== -0
            ? {
                ...basePayload,
                applications: [data.application.value.toString()], // Ensure value is string
                custom_application: null, // Explicitly null
              }
            : data.application?.value === -0 && data.custom_application
            ? {
                ...basePayload,
                applications: [],
                custom_application: data.custom_application,
              }
            : { ...basePayload, applications: [], custom_application: null }; // Default: empty/null

        await axios.post(
          endpoints.v1_storefronts_id_sampleRequests_id_items(
            storefront_id,
            transactionId
          ),
          payload
        );
        notifySuccess(t("Product added successfully"));
        setIsSaving(false);
        onComplete();
      } catch (error) {
        notifyError(t("Failed to add product"), { error });
        setIsSaving(false);
      }
    };

    const calculateTotalQuantity = (
      packageVolume: string | undefined,
      numberOfUnits: string
    ): string | null => {
      if (!packageVolume || !numberOfUnits) {
        return null;
      }

      const volume = parseFloat(packageVolume);
      const units = parseInt(numberOfUnits);

      if (isNaN(volume) || isNaN(units)) {
        return null;
      }

      return (volume * units).toString();
    };

    const totalQuantity = calculateTotalQuantity(
      packaging?.value?.package_volume,
      no_of_units
    );

    const totalQuantityString =
      totalQuantity === null
        ? "--"
        : formatTotalQuantityString(
            packaging?.value?.packaging_unit?.name,
            totalQuantity
          );

    return (
      <div>
        <Title>{t("Add Product")}</Title>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <SearchSelectInfiniteScroll
            name="product_id"
            errors={errors}
            formState={formState}
            placeholder={t("Product")}
            baseUrl={endpoints.v2_tenants_id_pim_products_summary(tenant_id)}
            params={(() => {
              const params = new URLSearchParams();
              params.append("order_by", "asc");
              params.append("published_products_only", "true");
              params.append("include_skus", "true");
              params.append("include_applications", "true");
              params.append("buyer_tenant_id", buyerId ?? tenant_id);
              return params;
            })()}
            getOptions={(response: PIMProductBase[]) =>
              response.map((product) => ({
                value: product,
                label: product.name,
              }))
            }
            value={{
              value: selectedProduct,
              label: selectedProduct ? selectedProduct.name : null,
            }}
            onChange={(data: OptionType<PIMProductBase>) =>
              handleProductChange(data)
            }
          />

          <Controller
            as={SelectBoxV2}
            control={control}
            name="packaging"
            placeholder={t("Select SKU")}
            options={productSkuOptions ? productSkuOptions : []}
            rules={{ required: strings(t).thisIsARequiredField }}
            errors={errors}
            formState={formState}
            isDisabled={!selectedProduct}
          />

          <TextField
            name="no_of_units"
            type="number"
            theref={register({
              required: true,
            })}
            label={t("Number of Units")}
            errors={errors}
            formState={formState}
          />

          <KeyValueDisplay
            data={[
              {
                key: t("Total Sample Quantity"),
                value: totalQuantityString,
              },
            ]}
          />

          <SmallSectionHeader>{t("Potential Annual Usage")}</SmallSectionHeader>
          <FormGrid2x2>
            <TextField
              name="total_annual_quantity"
              type="number"
              theref={register({
                required: true,
              })}
              label={t("Total Annual Quantity")}
              errors={errors}
              formState={formState}
            />
            <Controller
              as={SelectBoxV2}
              control={control}
              name="unit_of_measure"
              placeholder={t("Unit of Measure")}
              options={unitOfMeasureOptions}
              rules={{ required: true }}
              errors={errors}
              formState={formState}
            />
          </FormGrid2x2>

          {selectedProduct && (
            <ProductApplicationSelect
              methodsOfUseForm={methodsOfUseForm}
              applications={selectedProduct.product_applications}
            />
          )}

          <Controller
            as={SelectBoxV2}
            control={control}
            name="purpose"
            placeholder={t("Purpose")}
            options={SAMPLE_REQUEST_PURPOSES}
            rules={{ required: true }}
            errors={errors}
            formState={formState}
          />

          <PrimaryButtonFitContainer
            type="submit"
            loading={isSaving}
            onClick={() => {
              if (!selectedProduct?.id) {
                setError("product_id", {
                  type: "required",
                  message: strings(t).thisIsARequiredField,
                });
              }
            }}
          >
            {t("Add Product")}
          </PrimaryButtonFitContainer>
        </Form>
      </div>
    );
  };
